import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
interface IntegrationCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  isConnected: boolean;
  fields: {
    name: string;
    label: string;
    type?: string;
    placeholder?: string;
    value?: string;
    readonly?: boolean;
    helpText?: string;
  }[];
  onSave: (data: Record<string, string>) => Promise<void>;
  onTest?: () => Promise<boolean>;
  onDisconnect?: () => Promise<void>;
  helpLink: string;
  lastUpdated?: string;
}
export function IntegrationCard({
  title,
  description,
  icon: Icon,
  isConnected,
  fields,
  onSave,
  onTest,
  onDisconnect,
  helpLink,
  lastUpdated
}: IntegrationCardProps) {
  const [formData, setFormData] = useState<Record<string, string>>(fields.reduce((acc, field) => ({
    ...acc,
    [field.name]: field.value || ''
  }), {}));
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave(formData);
      alert('تم الحفظ بنجاح');
    } catch (error: any) {
      alert(error.message || 'فشل في الحفظ');
    } finally {
      setIsLoading(false);
    }
  };
  const handleTest = async () => {
    if (!onTest) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const success = await onTest();
      setTestResult(success ? 'success' : 'error');
    } catch (error) {
      setTestResult('error');
    } finally {
      setIsTesting(false);
    }
  };
  const handleDisconnect = async () => {
    if (!onDisconnect) return;
    const confirmed = window.confirm('هل أنت متأكد من فصل هذا الربط؟');
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await onDisconnect();
      alert('تم فصل الربط بنجاح');
    } catch (error: any) {
      alert(error.message || 'فشل في فصل الربط');
    } finally {
      setIsLoading(false);
    }
  };
  const maskValue = (value: string) => {
    if (!value || value.length < 8) return value;
    const lastFour = value.slice(-4);
    return '••••••••' + lastFour;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" dir="rtl" data-id="element-219">
      <div className="p-6 border-b border-gray-100" data-id="element-220">
        <div className="flex items-start justify-between" data-id="element-221">
          <div className="flex items-center gap-4" data-id="element-222">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg" data-id="element-223">
              <Icon size={24} data-id="element-224" />
            </div>
            <div data-id="element-225">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-226">{title}</h3>
              <p className="text-sm text-gray-500" data-id="element-227">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-228">
            {isConnected ? <span className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium" data-id="element-229">
                <CheckCircle2 size={16} data-id="element-230" />
                متصل
              </span> : <span className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium" data-id="element-231">
                <XCircle size={16} data-id="element-232" />
                غير متصل
              </span>}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4" data-id="element-233">
        {fields.map(field => <div key={field.name} data-id="element-234">
            {field.type === 'password' || field.name.toLowerCase().includes('secret') || field.name.toLowerCase().includes('token') ? <div className="relative" data-id="element-235">
                <Input label={field.label} type={showSecrets[field.name] ? 'text' : 'password'} placeholder={field.placeholder} value={field.readonly && field.value ? maskValue(field.value) : formData[field.name]} onChange={e => handleChange(field.name, e.target.value)} disabled={field.readonly} isRTL data-id="element-236" />
                {!field.readonly && <button type="button" onClick={() => setShowSecrets(prev => ({
            ...prev,
            [field.name]: !prev[field.name]
          }))} className="absolute left-3 top-9 text-gray-400 hover:text-gray-600" data-id="element-237">
                    {showSecrets[field.name] ? <EyeOff size={18} data-id="element-238" /> : <Eye size={18} data-id="element-239" />}
                  </button>}
              </div> : <Input label={field.label} type={field.type || 'text'} placeholder={field.placeholder} value={formData[field.name]} onChange={e => handleChange(field.name, e.target.value)} disabled={field.readonly} isRTL data-id="element-240" />}
            {field.helpText && <p className="text-xs text-gray-500 mt-1" data-id="element-241">{field.helpText}</p>}
          </div>)}

        {lastUpdated && <p className="text-xs text-gray-400" data-id="element-242">
            آخر تحديث: {new Date(lastUpdated).toLocaleString('ar-EG')}
          </p>}

        {testResult && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`p-3 rounded-lg ${testResult === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-243">
            {testResult === 'success' ? '✓ الاتصال ناجح!' : '✗ فشل الاتصال. تحقق من البيانات.'}
          </motion.div>}

        <div className="flex gap-3 pt-4" data-id="element-244">
          <Button onClick={handleSave} isLoading={isLoading} disabled={isConnected && !Object.values(formData).some(v => v)} className="flex-1" data-id="element-245">
            {isConnected ? 'تحديث' : 'حفظ وربط'}
          </Button>
          {onTest && <Button variant="outline" onClick={handleTest} isLoading={isTesting} disabled={!Object.values(formData).some(v => v)} data-id="element-246">
              اختبار الاتصال
            </Button>}
          {isConnected && onDisconnect && <Button variant="outline" onClick={handleDisconnect} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-id="element-247">
              فصل
            </Button>}
        </div>

        <a href={helpLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mt-2" data-id="element-248">
          كيف أحصل على هذه البيانات؟
          <ExternalLink size={14} data-id="element-249" />
        </a>
      </div>
    </motion.div>;
}